import React, { useRef } from "react";
import moment from "moment";
import {
  Card,
  Badge,
  CustomInput,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  CardBody,
  Button,
} from "reactstrap";
import { END_POINT } from "../../constants/defaultValues";
import classnames from "classnames";
import IntlMessages from "../../helpers/IntlMessages";
import STATUS from "../../constants/status";
import {
  filterOrdersById,
  isAllOrderCompleted,
  isIncludeOrderCompleted,
  isIncludeOrderCreated,
  isIncludeOrderPending,
} from "./helpers/utils";
import ReactToPrint from "react-to-print";
import ComponentToPrint from "../pages/ComponentToPrint";
import ComponentToPrintForKitchen from "../pages/ComponentToPrintForKitchen";
import { AddCommaNumber } from "../../helpers/Utils";
import ProductImage from "../../components/common/ProductImage";

/* alias
111: ĐANG CHỜ NHẬN
222: ĐÃ NHẬN, CHỜ GIAO,
444: ĐÃ GIAO, CHỜ XUẤT HÓA ĐƠN 
*/

const ListPendingOrdersStatus = ({
  orders,
  selectedItems,
  onCheckItem,
  onCheckMultiple,
  onActions,
  isMobile,
  alias,
  toggleModalInvoice,
  emptyText,
  itemsPendingBill,
  companyName,
  companyAddress,
  companyLogo,
  companyWebsite,
  // billDate,
  pin_print,
  handleClickedPrintInvoice,
  switchCheckedBillQr,
  toggleModalNested,
  switchCheckedOrderTwo,
  itemsTable,
}) => {
  // console.log("itemsTable: ", itemsTable);
  const items = selectedItems.map((itemId) => {
    const _order = orders.find((o) => o.id === itemId);
    return _order ? _order : null;
  });

  // Tạo một đối tượng để nhóm các orders theo user_identify
  const groupedOrders = {};
  for (const order of orders) {
    const userIdentify = order.user_identify;
    if (!groupedOrders[userIdentify]) {
      groupedOrders[userIdentify] = [];
    }
    groupedOrders[userIdentify].push(order);
  }

  // Chuyển đổi đối tượng nhóm thành mảng các nhóm
  const orderGroups = Object.values(groupedOrders);
  // console.log('orderGroups: ', orderGroups);
  const flattenedArray = orderGroups?.flat();
  // console.log('flattenedArray: ', flattenedArray);

  // Sử dụng reduce để tính tổng giá trị price khi id trùng với selectedItems
  const priceSelected = flattenedArray?.reduce((sum, item) => {
    if (selectedItems.includes(item.id)) {
      return sum + Number(item.price);
    }
    return sum;
  }, 0);

  // Hiển thị tổng giá trị
  // console.log('priceSelected: ', priceSelected);

  const totalPrice = flattenedArray?.reduce(
    (a, b) => Number(a) + Number(b.price),
    0,
  );

  // Helper function to check if all orders in a group are selected
  const isGroupSelected = (group) => {
    return group.every((order) => selectedItems.includes(order.id));
  };

  // Helper function to handle group checkbox click - uses batch selection
  const handleGroupCheck = (event, group) => {
    event.stopPropagation();
    const groupOrderIds = group.map((order) => order.id);
    const allSelected = isGroupSelected(group);

    // Use batch selection function if available
    if (onCheckMultiple) {
      // If all selected, deselect all. If not all selected, select all.
      onCheckMultiple(groupOrderIds, !allSelected);
    } else {
      // Fallback to individual selection (should not happen normally)
      groupOrderIds.forEach((orderId) => {
        if (allSelected) {
          if (selectedItems.includes(orderId)) {
            onCheckItem(event, orderId);
          }
        } else {
          if (!selectedItems.includes(orderId)) {
            onCheckItem(event, orderId);
          }
        }
      });
    }
  };

  const handleCompleteOnlineOrder = (event, orderIds) => {
    event.stopPropagation();
    if (!Array.isArray(orderIds) || orderIds.length < 1) return;
    onActions(orderIds, "end");
  };

  const handleCancelOnlineOrder = (event, orderIds) => {
    event.stopPropagation();
    if (!Array.isArray(orderIds) || orderIds.length < 1) return;
    onActions(orderIds, "destroy");
  };

  const handleAddMoreProduct = (event) => {
    // Placeholder: business logic thêm món sẽ triển khai sau
    event.stopPropagation();
  };

  const normalizeOnlineInfo = (value) => {
    const raw = String(value ?? "").trim();
    if (!raw || raw.toLowerCase() === "null" || raw.toLowerCase() === "undefined") {
      return "--";
    }
    return raw;
  };

  const renderOnlineOrderMeta = (order) => {
    if (alias !== 111 || !order || order.user_identify !== null) {
      return null;
    }

    const customerName = normalizeOnlineInfo(order.customer_name);
    const customerPhone = normalizeOnlineInfo(order.phone);
    const deliveryTime = normalizeOnlineInfo(order.delivery_time);
    const customerAddress = normalizeOnlineInfo(order.customer_address);

    return (
      <div className="mt-2 text-small text-muted">
        <div>{`${customerName} - ${customerPhone} - ${deliveryTime}`}</div>
        <div className="break-word">
          <strong>Địa chỉ:</strong> {customerAddress}
        </div>
      </div>
    );
  };

  const getOrderOptionGroupKey = (order = {}) => {
    const productId = order?.product_id ?? order?.product?.id ?? "";
    const size = order?.size ?? "";
    const ice = order?.ice ?? "";
    const sugar = order?.sugar ?? "";
    const topping = order?.topping ?? "";
    const note = order?.note ?? order?.notes ?? "";
    const unitPrice = Number(order?.price ?? 0);

    return [
      productId,
      size,
      ice,
      sugar,
      topping,
      note,
      unitPrice,
      order?.status_id ?? order?.status?.uuid ?? "",
    ].join("|");
  };

  const buildGroupedRowsByOption = (ordersInBill = []) => {
    const groupedMap = new Map();

    ordersInBill.forEach((order) => {
      const optionKey = getOrderOptionGroupKey(order);

      if (!groupedMap.has(optionKey)) {
        groupedMap.set(optionKey, {
          key: optionKey,
          order,
          orderIds: [],
          quantity: 0,
          totalPrice: 0,
        });
      }

      const row = groupedMap.get(optionKey);
      const qty = Number(order?.quantity) > 0 ? Number(order.quantity) : 1;
      row.orderIds.push(order.id);
      row.quantity += qty;
      row.totalPrice += Number(order?.price ?? 0);
    });

    return Array.from(groupedMap.values());
  };

  const OrdersAtBar = ({ array }) => {
    // Ưu tiên nhóm theo bill để tránh trộn nhiều khách online cùng khung giờ
    const groupedByBill = new Map();

    array.forEach((item, idx) => {
      const groupKey =
        item.bill_id ||
        item?.bill?.id ||
        `${item.date || ""}_${item.time || ""}_${item.id || idx}`;

      if (groupedByBill.has(groupKey)) {
        groupedByBill.get(groupKey).push(item);
      } else {
        groupedByBill.set(groupKey, [item]);
      }
    });

    // Chuyển các giá trị từ map sang mảng kết quả
    const resultArray = [...groupedByBill.values()];

    // console.log(resultArray);
    return resultArray.map((result, idx) => {
      const groupedRows =
        alias === 111
          ? buildGroupedRowsByOption(result)
          : result.map((order) => ({
              key: `order_${order.id}`,
              order,
              orderIds: [order.id],
              quantity: Number(order?.quantity) > 0 ? Number(order.quantity) : 1,
              totalPrice: Number(order?.price ?? 0),
            }));

      const selectedOrderIdsInGroup = result
        .map((order) => order?.id)
        .filter((id) => id !== null && id !== undefined && selectedItems.includes(id));

      return (
        <Card key={idx} className="mb-3">
          {alias === 444 ? (
            // Group checkbox for pending payment
            <div
              className="p-3 d-flex justify-content-between align-items-center cursor-pointer"
              onClick={(e) => handleGroupCheck(e, result)}
            >
              <div className="flex-grow-1 pr-2">
                <h5 className="mb-0">
                  <IntlMessages id="dashboards.counter" />
                </h5>
                {renderOnlineOrderMeta(result[0])}
              </div>
              <div className="d-flex align-items-center">
                {result.length > 1 && (
                  <>
                    <span className="mr-2 text-small">Chọn tất cả</span>
                    <div className="custom-control custom-checkbox pl-1">
                      <CustomInput
                        className="item-check mb-0"
                        type="checkbox"
                        id={`group_counter_${idx}`}
                        checked={isGroupSelected(result)}
                        onChange={() => {}}
                        label=""
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            // Header with group checkbox for 111/222
            <div
              className="p-3 d-flex justify-content-between align-items-center cursor-pointer"
              onClick={(e) => handleGroupCheck(e, result)}
            >
              <div className="flex-grow-1 pr-2">
                <h5 className="mb-0">
                  <IntlMessages id="dashboards.counter" />
                </h5>
                {renderOnlineOrderMeta(result[0])}
              </div>
              <div className="d-flex align-items-center">
                {result.length > 1 && (
                  <>
                    <span className="mr-2 text-small">Chọn tất cả</span>
                    <div className="custom-control custom-checkbox pl-1">
                      <CustomInput
                        className="item-check mb-0"
                        type="checkbox"
                        id={`group_counter_all_${idx}`}
                        checked={isGroupSelected(result)}
                        onChange={() => {}}
                        label=""
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          {groupedRows.map((row) => (
            <OrderCard
              key={row.key}
              order={row.order}
              orderIds={row.orderIds}
              quantity={row.quantity}
              totalPrice={row.totalPrice}
              showQuantityPrefix={alias === 111}
              selectedItems={selectedItems}
              onCheckItem={onCheckItem}
              onCheckMultiple={onCheckMultiple}
              group={result}
            />
          ))}
          <div className="px-3 pb-3 mt-2 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Button
                size="sm"
                color="primary"
                outline
                className="mr-2"
                onClick={handleAddMoreProduct}
              >
                Thêm món
              </Button>
              <Button
                size="sm"
                color="danger"
                outline
                className="mr-2"
                disabled={selectedOrderIdsInGroup.length < 1}
                onClick={(e) =>
                  handleCancelOnlineOrder(e, selectedOrderIdsInGroup)
                }
              >
                Hủy món
              </Button>
            </div>
            <Button
              size="sm"
              color="success"
              disabled={selectedOrderIdsInGroup.length < 1}
              onClick={(e) =>
                handleCompleteOnlineOrder(e, selectedOrderIdsInGroup)
              }
            >
              Hoàn tất đơn
            </Button>
          </div>
        </Card>
      );
    });
  };

  // Hiển thị các nhóm bằng cách tạo các <Card /> tương ứng
  const OrderCard = ({
    order,
    selectedItems,
    onCheckItem,
    onCheckMultiple,
    orderIds = null,
    quantity = 1,
    totalPrice = null,
    showQuantityPrefix = false,
  }) => {
    const toppings = order?.topping?.split("|");
    const currentOrderIds =
      Array.isArray(orderIds) && orderIds.length > 0 ? orderIds : [order.id];
    const isChecked = currentOrderIds.every((id) => selectedItems.includes(id));
    const isCheckedAny = currentOrderIds.some((id) => selectedItems.includes(id));
    const displayPrice =
      showQuantityPrefix && Number(quantity) > 1
        ? Number(totalPrice ?? order?.price ?? 0)
        : Number(order?.price ?? 0);

    const toggleSelection = (event) => {
      event.stopPropagation();
      const nextChecked = !isChecked;

      if (currentOrderIds.length > 1 && onCheckMultiple) {
        onCheckMultiple(currentOrderIds, nextChecked);
        return;
      }

      currentOrderIds.forEach((id) => {
        const selected = selectedItems.includes(id);
        if ((nextChecked && !selected) || (!nextChecked && selected)) {
          onCheckItem(event, id);
        }
      });
    };

    return (
      <div
        onClick={toggleSelection}
        className={classnames("d-flex flex-row cursor-pointer", {
          active: isCheckedAny,
        })}
      >
        <div className="d-block position-relative">
          <ProductImage
            image={order.product.image}
            alt={order.product.name}
            fnbType={order.product.fnb_type}
            className="list-thumbnail border-0 m-2"
            iconSize="40px"
            style={{
              width: isMobile ? "120px" : undefined,
              height: isMobile ? "90px" : undefined,
              objectFit: "cover",
            }}
          />
          <Badge
            className="position-absolute badge-top-right m-2"
            color={
              order.status.uuid === STATUS.CREATED
                ? "danger"
                : order.status.uuid === STATUS.CLOSED ||
                    order.status.uuid === STATUS.DESTROY
                  ? "secondary"
                  : order.status.uuid === STATUS.COMPLETED ||
                      order.status.uuid === STATUS.PAYMENTED
                    ? "success"
                    : "primary"
            }
            pill
          >
            {order.status.name}
          </Badge>
        </div>
        <div className="card-body-custom d-flex flex-column justify-content-between min-width-zero p-05rem">
          <section>
            <div className="d-flex justify-content-between">
              <span className="list-item-heading">
                {showQuantityPrefix ? (
                  <span className="text-primary mr-1">{`x${quantity}`}</span>
                ) : null}
                {order.product.name}
              </span>
              <span>
                <Badge color="primary">{AddCommaNumber(displayPrice)} đ</Badge>
              </span>
            </div>
            <div className="text-primary text-small font-weight-medium d-sm-block">
              {order.date + " - " + order.time}
            </div>
          </section>
          <div className="w-100">
            {order.size === "" || order?.size?.split(",")[1] === "0" ? null : (
              <Badge className="mr-1 mt-1" color="warning">
                {order?.size?.split(",")[0]}
              </Badge>
            )}
            {order.flavor ? (
              <Badge className="mr-1 mt-1" color="info">
                Vị: {order.flavor.split(",")[0]}
              </Badge>
            ) : null}
            {order.ice === "" || order.ice === "100" ? null : (
              <Badge className="mr-1 mt-1" color="primary">
                <IntlMessages id="dashboards.ice" />: {order.ice}%
              </Badge>
            )}
            {order.sugar === "" || order.sugar === "100" ? null : (
              <Badge className="mr-1 mt-1" color="info">
                <IntlMessages id="dashboards.ice" />: {order.sugar}%
              </Badge>
            )}
            {order.topping === "" || order.topping.split(",")[1] === "0"
              ? null
              : toppings.map((_topping, idx) => (
                  <Badge color="success" className="mr-1 mt-1" key={idx}>
                    {_topping.split(",")[0]}
                  </Badge>
                ))}
          </div>
        </div>
        <div
          className="custom-control custom-checkbox pl-1 align-self-center pr-1"
          onClick={(e) => e.stopPropagation()}
        >
          <CustomInput
            className="mb-0"
            type="checkbox"
            id={`order_status_${order.id}`}
            checked={isChecked}
            onChange={toggleSelection}
            label=""
          />
        </div>
      </div>
    );
  };

  let componentRef = useRef();
  let componentRefKitchen = useRef(null);
  const prepareInvoiceForPrint = async () => {
    if (
      componentRef.current &&
      typeof componentRef.current.prepareForPrint === "function"
    ) {
      await componentRef.current.prepareForPrint();
    }
  };

  return (
    <div
      className={classnames("disable-text-selection", {
        "inline-flex": !isMobile,
      })}
    >
      <div
        className={classnames("mb-3 custom-shadow mt-2 card-bill-items", {
          "card-bill-items-desktop": !isMobile,
        })}
      >
        <div className="position-absolute card-top-buttons">
          {isMobile ? (
            <UncontrolledDropdown>
              <DropdownToggle
                color=""
                className="btn btn-header-light icon-button"
              >
                <span className="simple-icon-options" />
              </DropdownToggle>
              {selectedItems.length ? (
                <DropdownMenu right>
                  {!isIncludeOrderCompleted(
                    filterOrdersById(orders, selectedItems),
                  ) ? (
                    <>
                      {alias === 111 &&
                      !isIncludeOrderPending(
                        filterOrdersById(orders, selectedItems),
                      ) ? (
                        <DropdownItem
                          onClick={() => {
                            onActions(selectedItems, "pending");
                          }}
                        >
                          <IntlMessages id="order.to-pending" />
                        </DropdownItem>
                      ) : null}
                      {alias === 222 &&
                      !isIncludeOrderCreated(
                        filterOrdersById(orders, selectedItems),
                      ) ? (
                        <DropdownItem
                          onClick={() => {
                            onActions(selectedItems, "done");
                          }}
                        >
                          <IntlMessages id="order.to-done" />
                        </DropdownItem>
                      ) : null}
                      {alias !== 444 ? (
                        <>
                          <DropdownItem
                            onClick={() => {
                              onActions(selectedItems, "refund");
                            }}
                          >
                            <IntlMessages id="order.to-refund" />
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => {
                              onActions(selectedItems, "destroy");
                            }}
                          >
                            <IntlMessages id="order.to-destroy" />
                          </DropdownItem>
                        </>
                      ) : null}
                    </>
                  ) : null}
                  {alias === 444 &&
                  isAllOrderCompleted(
                    filterOrdersById(orders, selectedItems),
                  ) ? (
                    <>
                      <DropdownItem
                        onClick={() => {
                          onActions(selectedItems, "end");
                        }}
                      >
                        <IntlMessages id="order.to-end" />
                      </DropdownItem>
                      <DropdownItem
                        onClick={() => {
                          toggleModalInvoice({
                            orders: selectedItems,
                          });
                        }}
                      >
                        <IntlMessages id="order.to-bill" />
                      </DropdownItem>
                    </>
                  ) : null}
                </DropdownMenu>
              ) : (
                <></>
              )}
            </UncontrolledDropdown>
          ) : null}
        </div>
        {!orderGroups.length ? (
          <Card>
            <CardBody className="text-center align-center">
              <IntlMessages id="dashboards.list-empty" />
            </CardBody>
          </Card>
        ) : (
          <>
            {alias === 444 ? (
              <div className="d-flex justify-content-around align-items-center text-center w-100 mb-3">
                <span>
                  <IntlMessages id="dashboards.total" />:
                </span>
                <span className="text-primary">
                  {priceSelected > 0 ? (
                    <div className="d-flex align-items-end">
                      <h2 className="mb-1">
                        {AddCommaNumber(priceSelected)} đ{" "}
                      </h2>{" "}
                      / <div>{AddCommaNumber(totalPrice)} đ</div>
                    </div>
                  ) : (
                    `${AddCommaNumber(totalPrice)} đ`
                  )}
                </span>
              </div>
            ) : null}
            {orderGroups.map((group) =>
              group[0].user_identify === null ? (
                <OrdersAtBar array={group} key={group[0].user_identify} />
              ) : (
                <Card key={group[0].user_identify} className="mb-3">
                  {alias === 444 ? (
                    // Group checkbox for pending payment
                    <div
                      className="p-3 d-flex justify-content-between align-items-center cursor-pointer"
                      onClick={(e) => handleGroupCheck(e, group)}
                    >
                      <h5 className="mb-0 flex-grow-1">
                        {group[0].user_identify === null ? (
                          <IntlMessages id="dashboards.counter" />
                        ) : (
                          <>
                            <IntlMessages id="dashboards.table" />{" "}
                            {group[0].user_identify}
                          </>
                        )}
                      </h5>
                      {group.length > 1 && (
                        <div className="d-flex align-items-center">
                          <span className="mr-2 text-small">Chọn tất cả</span>
                          <div className="custom-control custom-checkbox pl-1">
                            <CustomInput
                              className="item-check mb-0"
                              type="checkbox"
                              id={`group_${group[0].user_identify}`}
                              checked={isGroupSelected(group)}
                              onChange={() => {}}
                              label=""
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Header with group checkbox for 111/222
                    <div
                      className="p-3 d-flex justify-content-between align-items-center cursor-pointer"
                      onClick={(e) => handleGroupCheck(e, group)}
                    >
                      <h5 className="mb-0 flex-grow-1">
                        {group[0].user_identify === null ? (
                          <IntlMessages id="dashboards.counter" />
                        ) : (
                          <>
                            <IntlMessages id="dashboards.table" />{" "}
                            {group[0].user_identify}
                          </>
                        )}
                      </h5>
                      {group.length > 1 && (
                        <div className="d-flex align-items-center">
                          <span className="mr-2 text-small">Chọn tất cả</span>
                          <div className="custom-control custom-checkbox pl-1">
                            <CustomInput
                              className="item-check mb-0"
                              type="checkbox"
                              id={`group_all_${group[0].user_identify}`}
                              checked={isGroupSelected(group)}
                              onChange={() => {}}
                              label=""
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {group.map((order) => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      selectedItems={selectedItems}
                      onCheckItem={onCheckItem}
                      group={group}
                    />
                  ))}
                </Card>
              ),
            )}
          </>
        )}
      </div>
      {!isMobile ? (
        <div className="pl-2">
          <section className="mb-2">
            {alias === 111 ? (
              selectedItems.length &&
              !isIncludeOrderPending(filterOrdersById(orders, selectedItems)) &&
              !isIncludeOrderCompleted(
                filterOrdersById(orders, selectedItems),
              ) ? (
                switchCheckedBillQr ? (
                  componentRefKitchen && (
                    <ReactToPrint
                      trigger={() => (
                        <div className="card-bill-actions">
                          <div
                            className="card-bill-actions-active"
                            onClick={() => {
                              onActions(selectedItems, "pending");
                            }}
                          >
                            <IntlMessages id="order.to-pending" />
                          </div>
                        </div>
                      )}
                      content={() => componentRefKitchen.current}
                    />
                  )
                ) : (
                  <div className="card-bill-actions">
                    <div
                      className="card-bill-actions-active"
                      onClick={() => {
                        onActions(selectedItems, "pending");
                      }}
                    >
                      <IntlMessages id="order.to-pending" />
                    </div>
                  </div>
                )
              ) : (
                <div className="card-bill-actions">
                  <div className="text-muted">
                    <IntlMessages id="order.to-pending" />
                  </div>
                </div>
              )
            ) : null}

            <div style={{ display: "none" }}>
              <ComponentToPrintForKitchen
                items={items}
                // billDate={billDate}
                pin_print={pin_print}
                // discount={discount}
                ref={componentRefKitchen}
                style={{ display: "none" }}
              />
            </div>

            {alias === 222 ? (
              selectedItems.length &&
              !isIncludeOrderCreated(filterOrdersById(orders, selectedItems)) &&
              !isIncludeOrderCompleted(
                filterOrdersById(orders, selectedItems),
              ) ? (
                <div className="card-bill-actions">
                  <div
                    className="card-bill-actions-active"
                    onClick={() => {
                      onActions(selectedItems, "done");
                    }}
                  >
                    {switchCheckedOrderTwo ? (
                      <IntlMessages id="order.done" />
                    ) : (
                      <IntlMessages id="order.to-done" />
                    )}
                  </div>
                </div>
              ) : (
                <div className="card-bill-actions">
                  <div className="text-muted">
                    {switchCheckedOrderTwo ? (
                      <IntlMessages id="order.done" />
                    ) : (
                      <IntlMessages id="order.to-done" />
                    )}
                  </div>
                </div>
              )
            ) : null}
          </section>

          {alias === 111 || alias === 222 ? (
            <div className="custom-line mb-3 mt-3 w-80 m-0-auto" />
          ) : null}

          <section className="mb-2">
            {alias !== 444 ? (
              selectedItems.length &&
              !isIncludeOrderCompleted(
                filterOrdersById(orders, selectedItems),
              ) ? (
                <div className="card-bill-actions">
                  <div
                    className="card-bill-actions-active"
                    onClick={() => {
                      onActions(selectedItems, "refund");
                    }}
                  >
                    <IntlMessages id="order.to-refund" />
                  </div>
                </div>
              ) : (
                <div className="card-bill-actions">
                  <div className="text-muted">
                    <IntlMessages id="order.to-refund" />
                  </div>
                </div>
              )
            ) : null}
            {alias !== 444 ? (
              selectedItems.length &&
              !isIncludeOrderCompleted(
                filterOrdersById(orders, selectedItems),
              ) ? (
                <div className="card-bill-actions">
                  <div
                    className="card-bill-actions-active"
                    onClick={() => {
                      onActions(selectedItems, "destroy");
                    }}
                  >
                    <IntlMessages id="order.to-destroy" />
                  </div>
                </div>
              ) : (
                <div className="card-bill-actions">
                  <div className="text-muted">
                    <IntlMessages className="mb-1" id="order.to-destroy" />
                  </div>
                </div>
              )
            ) : null}
          </section>

          {/* <div className="custom-line mb-3 mt-3 w-80 m-0-auto" />  */}

          <section className="mb-2">
            {alias === 444 ? (
              selectedItems.length &&
              isAllOrderCompleted(filterOrdersById(orders, selectedItems)) ? (
                <div className="card-bill-actions">
                  <div
                    className="card-bill-actions-active"
                    onClick={() => {
                      onActions(selectedItems, "end");
                    }}
                  >
                    <IntlMessages id="order.to-end" />
                  </div>
                </div>
              ) : (
                <div className="card-bill-actions">
                  <div className="text-muted">
                    <IntlMessages id="order.to-end" />
                  </div>
                </div>
              )
            ) : null}
            {alias === 444 ? (
              selectedItems.length &&
              isAllOrderCompleted(filterOrdersById(orders, selectedItems)) ? (
                // <div className='card-bill-actions'>
                //     <div
                //         className='card-bill-actions-active'
                //         onClick={() => {
                //             toggleModalInvoice({
                //                 orders: selectedItems,
                //             });
                //        }}
                //     >
                //         <IntlMessages id='order.to-bill' />
                //     </div>
                // </div>
                componentRef && (
                  <ReactToPrint
                    onClick={(e) => handleClickedPrintInvoice(e, selectedItems)}
                    trigger={() => (
                      <div className="card-bill-actions">
                        <div
                          className="card-bill-actions-active"
                          onClick={(e) =>
                            handleClickedPrintInvoice(e, selectedItems)
                          }
                        >
                          <IntlMessages id="order.to-bill" />
                        </div>
                      </div>
                    )}
                    content={() => componentRef.current}
                    onBeforeGetContent={prepareInvoiceForPrint}
                  />
                )
              ) : (
                <div className="card-bill-actions">
                  <div className="text-muted">
                    <IntlMessages id="order.to-bill" />
                  </div>
                </div>
              )
            ) : null}
            {/* {alias === 444 ? (
              <div className="custom-line mb-3 mt-3 w-80 m-0-auto" />
            ) : null}
            {alias === 444 ? (
              selectedItems.length &&
              isAllOrderCompleted(filterOrdersById(orders, selectedItems)) ? (
                <div className="card-bill-actions">
                  <div
                    className="card-bill-actions-active"
                    onClick={() => {
                      onActions(selectedItems, "change");
                    }}
                  >
                    <IntlMessages id="order.to-change" />
                  </div>
                </div>
              ) : (
                <div className="card-bill-actions">
                  <div className="text-muted">
                    <IntlMessages id="order.to-change" />
                  </div>
                </div>
              )
            ) : null} */}
            {alias === 444 && itemsTable?.maxFloor > 0 ? (
              <>
                <div className="custom-line mb-3 mt-3 w-80 m-0-auto" />
                {selectedItems.length &&
                isAllOrderCompleted(filterOrdersById(orders, selectedItems)) ? (
                  <div className="card-bill-actions">
                    <div
                      className="card-bill-actions-active"
                      onClick={() => {
                        toggleModalNested();
                      }}
                    >
                      <IntlMessages id="order.transfer-table" />
                    </div>
                  </div>
                ) : (
                  <div className="card-bill-actions">
                    <div className="text-muted">
                      <IntlMessages id="order.transfer-table" />
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </section>
          <div className="custom-line mb-3 mt-3 w-80 m-0-auto" />
          <section className="mb-2">
            {(selectedItems.length &&
              !isIncludeOrderCompleted(
                filterOrdersById(orders, selectedItems),
              )) ||
            (selectedItems.length &&
              isAllOrderCompleted(filterOrdersById(orders, selectedItems))) ? (
              componentRef && (
                <ReactToPrint
                  trigger={() => (
                    <div className="card-bill-actions">
                      <div className="card-bill-actions-active">
                        <IntlMessages id="order.to-bill-draft" />
                      </div>
                    </div>
                  )}
                  content={() => componentRef.current}
                  onBeforeGetContent={prepareInvoiceForPrint}
                />
              )
            ) : (
              // <div className='card-bill-actions'>
              //     <div
              //         className='card-bill-actions-active'
              //         onClick={() => {
              //             onActions(selectedItems, 'bill-draft');
              //         }}
              //     >
              //         <IntlMessages id='order.to-bill-draft' />
              //     </div>
              // </div>
              <div className="card-bill-actions">
                <div className="text-muted">
                  <IntlMessages className="mb-1" id="order.to-bill-draft" />
                </div>
              </div>
            )}
          </section>
          <div style={{ display: "none" }}>
            <ComponentToPrint
              alias={alias}
              items={items}
              companyName={companyName}
              companyAddress={companyAddress}
              companyLogo={companyLogo}
              companyWebsite={companyWebsite}
              billDate={
                items && items.length > 0 && items[0] && items[0].date
                  ? moment(items[0].date).format("DD/MM/YYYY")
                  : undefined
              }
              timeStart={
                items && items.length > 0 && items[0] ? items[0].time : null
              }
              pin_print={pin_print}
              ref={componentRef}
              // discount={
              //     discount_form.saved ? discount_form : null
              // }
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ListPendingOrdersStatus;
